import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { THEMES, DEFAULT_THEME_ID } from './themes';

const ThemeContext = createContext(null);

function applyTheme(theme) {
  const r = document.documentElement.style;
  const { sidebar, main } = theme;
  r.setProperty('--sidebar-bg', sidebar.bg);
  r.setProperty('--sidebar-text', sidebar.text);
  r.setProperty('--sidebar-text-muted', sidebar.textMuted);
  r.setProperty('--sidebar-accent', sidebar.accent);
  r.setProperty('--sidebar-nav-active-bg', sidebar.navActiveBg);
  r.setProperty('--sidebar-nav-active-border', sidebar.navActiveBorder);
  r.setProperty('--sidebar-section-label', sidebar.sectionLabel);
  r.setProperty('--sidebar-logo-bg', sidebar.logoBg);
  r.setProperty('--sidebar-logo-color', sidebar.logoColor);
  r.setProperty('--sidebar-user-av-bg', sidebar.userAvBg);
  r.setProperty('--sidebar-user-av-color', sidebar.userAvColor);
  r.setProperty('--sidebar-border', sidebar.border);
  r.setProperty('--sidebar-sub-menu-bg', sidebar.subMenuBg);
  r.setProperty('--sidebar-hover-bg', sidebar.hoverBg);
  r.setProperty('--main-bg', main.bg);
  r.setProperty('--topbar-bg', main.topbarBg);
  r.setProperty('--topbar-border', main.topbarBorder);
  r.setProperty('--topbar-text', main.topbarText);
  r.setProperty('--card-bg', main.cardBg);
  r.setProperty('--card-border', main.cardBorder);
  r.setProperty('--accent', main.accent);
  r.setProperty('--btn-bg', main.btnBg);
  r.setProperty('--btn-color', main.btnColor);
  r.setProperty('--text-primary', main.textPrimary);
  r.setProperty('--text-secondary', main.textSecondary);
  r.setProperty('--text-muted', main.textMuted);
  r.setProperty('--table-head-bg', main.tableHeadBg);
  r.setProperty('--row-hover', main.rowHover);
  r.setProperty('--title-color', main.titleColor || main.textPrimary);
  // Extra per-theme vars (e.g. Cupertino)
  if (theme.cssVars) {
    Object.entries(theme.cssVars).forEach(([k, v]) => r.setProperty(k, v));
  }
  // Theme ID attribute for CSS targeting
  document.documentElement.setAttribute('data-theme', theme.id);
}

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => {
    try { return localStorage.getItem('pms_theme') || DEFAULT_THEME_ID; }
    catch { return DEFAULT_THEME_ID; }
  });

  const activeTheme = THEMES.find(t => t.id === themeId) || THEMES[0];

  useEffect(() => {
    applyTheme(activeTheme);
  }, [activeTheme]);

  const switchTheme = useCallback((id) => {
    setThemeId(id);
    try { localStorage.setItem('pms_theme', id); } catch {}
  }, []);

  return (
    <ThemeContext.Provider value={{ themeId, activeTheme, themes: THEMES, switchTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
